import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../LanguageSwitcher';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Anchor links only work on the landing page where the sections exist.
  // On sub-pages we navigate to /#section instead.
  const isLandingPage = location.pathname === '/';

  const NAV_LINKS = [
    { label: t('landing.nav.home'), href: '#home' },
    { label: t('landing.nav.solutions'), href: '#solutions' },
    { label: t('landing.nav.howItWorks'), href: '#how-it-works' },
    { label: t('landing.nav.demo'), href: '#invoice-demo' },
    { label: t('landing.nav.useCases'), href: '#use-cases' },
    { label: t('landing.nav.benefits'), href: '#benefits' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On the landing page, smooth-scroll to the anchor.
  // On sub-pages, navigate to /#section so the landing page loads and scrolls.
  const handleAnchorClick = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (isLandingPage) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/${href}`);
    }
  };

  // Logo: always go home. On the landing page, scroll to top; on sub-pages, navigate.
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (isLandingPage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-white/[0.08] bg-[#050816]/80 backdrop-blur-xl' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-8 px-6 py-4 lg:px-8">
        <a href="/" onClick={handleLogoClick} className="flex shrink-0 items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-button">
            <span className="text-base font-bold text-white">A</span>
          </div>
          <span className="text-xl font-bold tracking-tight text-white">Alenis</span>
        </a>

        <nav className="hidden items-center gap-4 xl:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={isLandingPage ? link.href : `/${link.href}`}
              onClick={handleAnchorClick(link.href)}
              className="whitespace-nowrap text-sm font-medium text-brand-muted transition-colors hover:text-white"
            >
              {link.label}
            </a>
          ))}
          <Link to="/about" className="whitespace-nowrap text-sm font-medium text-brand-muted transition-colors hover:text-white">
            {t('landing.nav.about')}
          </Link>
          <Link to="/contact" className="whitespace-nowrap text-sm font-medium text-brand-muted transition-colors hover:text-white">
            {t('landing.nav.contact')}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <LanguageSwitcher variant="dark" />
          <button
            onClick={() => {
              trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'navbar', target: '/demo' });
              navigate('/demo');
            }}
            className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-brand-button px-5 py-2.5 text-sm font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.03]"
          >
            {t('landing.nav.cta')}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LanguageSwitcher variant="dark" />
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-white/[0.08] bg-[#050816]/95 backdrop-blur-xl xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={isLandingPage ? link.href : `/${link.href}`}
                  onClick={handleAnchorClick(link.href)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-brand-muted hover:bg-white/[0.04] hover:text-white"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/about"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-brand-muted hover:bg-white/[0.04] hover:text-white"
              >
                {t('landing.nav.about')}
              </Link>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-brand-muted hover:bg-white/[0.04] hover:text-white"
              >
                {t('landing.nav.contact')}
              </Link>
              <button
                onClick={() => {
                  setOpen(false);
                  trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'navbar_mobile', target: '/demo' });
                  navigate('/demo');
                }}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-button px-5 py-3 text-sm font-semibold text-white shadow-glow"
              >
                {t('landing.nav.cta')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
